/** @jsxImportSource react */
import { useEffect, useState } from 'react';

const CARE_SCOUT_WIDGET_ORIGIN = 'https://www.carescout.com';
const CARE_SCOUT_WIDGET_HEIGHT_EVENT =
  'careScoutCostOfCareWidgetContentHeightUpdate';
const DEFAULT_IFRAME_HEIGHT = 1400;
const MINIMUM_IFRAME_HEIGHT = 852;

type CareScoutHeightMessage = {
  type?: unknown;
  height?: unknown;
};

function CareScoutCostOfCareIcon() {
  return (
    <svg
      id='cs-cost-of-care-link-svg'
      width='44'
      height='44'
      viewBox='0 0 44 44'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
      className='h-11 w-11 flex-shrink-0'
    >
      <rect width='44' height='44' rx='8' fill='#7B40D5' />
      <path
        fill='#fff'
        d='M22.3318 10.0447C28.7834 10.0448 33.0267 13.3887 33.8072 18.4925c.0332.2148-.1365.4051-.3537.4051h-4.8569c-.1695 0-.3154-.118-.3614-.2814-.6701-2.389-2.7616-3.8863-5.8706-3.8864-4.263 0-6.718 2.8436-6.718 7.2702 0 4.4264 2.455 7.2692 6.718 7.2692 3.1091 0 5.2005-1.4972 5.8706-3.8864.0456-.1634.1919-.2814.3614-.2814h4.8569c.2168 0 .3864.1904.3537.4051-.7804 5.1039-5.0241 8.4487-11.4754 8.4487-7.4279 0-12.1432-5.0097-12.1432-11.9562 0-6.9464 4.7153-11.9543 12.1432-11.9543z'
      />
    </svg>
  );
}

function isCareScoutHeightMessage(
  data: unknown
): data is CareScoutHeightMessage {
  return typeof data === 'object' && data !== null;
}

export default function CareScoutCostOfCareWidget() {
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== CARE_SCOUT_WIDGET_ORIGIN) return;
      if (!isCareScoutHeightMessage(event.data)) return;
      if (event.data.type !== CARE_SCOUT_WIDGET_HEIGHT_EVENT) return;

      const nextHeight = Number(event.data.height);

      if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;

      setIframeHeight(nextHeight);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <div id='cs-cost-of-care-widget-wrapper' className='space-y-6'>
      <iframe
        id='cs-cost-of-care-widget-iframe'
        title='CareScout Cost of Care Widget'
        src='https://www.carescout.com/cost-of-care/widget?embed=1&lang=en'
        width='100%'
        loading='lazy'
        referrerPolicy='origin-when-cross-origin'
        sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
        className='block w-full border-0'
        style={{
          height: `${iframeHeight}px`,
          minHeight: `${MINIMUM_IFRAME_HEIGHT}px`,
        }}
      />

      <div
        id='cs-cost-of-care-link-wrapper'
        className='flex justify-center px-5 pb-2'
      >
        <a
          id='cs-cost-of-care-link'
          href='https://www.carescout.com/cost-of-care?utm_source=coc-widget&utm_medium=referral&utm_campaign=2026_03_aw_wgt-embr_cs-promo_nat_all_all&utm_content=back-link'
          target='_blank'
          rel='noopener'
          className='flex max-w-[415px] items-center justify-center gap-2 text-slate-base no-underline hover:no-underline'
        >
          <div id='cs-cost-of-care-link-svg-wrapper' className='block'>
            <CareScoutCostOfCareIcon />
          </div>
          <div id='cs-cost-of-care-link-text-wrapper' className='space-y-1'>
            <p
              id='cs-cost-of-care-link-text-header'
              className='m-0 p-0 font-semibold'
            >
              CareScout&apos;s guide to cost of long-term care
            </p>
            <p id='cs-cost-of-care-link-text' className='m-0 p-0 font-medium'>
              Long-term care can be expensive, learn more about Care Plans, cost
              of care trends and insights.
            </p>
          </div>
        </a>
      </div>
    </div>
  );
}
